import './styles/tokens.css';
import './styles/app.css';

import { defaultSlug, knownSlugs, packs, resolvePack } from '../projects/registry.js';
import { validatePack, validateRegistry } from './engine/validate.js';
import { renderApp } from './ui/app.js';
import { createStore } from './ui/store.js';
import { shareUrl } from './ui/url.js';
import { evaluateMetric, parseCached } from './engine/evaluate.js';
import { provenanceOf } from './engine/provenance.js';
import { formatRange } from './ui/num.js';
import { isUndisclosed, type Range } from './engine/schema.js';

// Fail loudly in development if a pack is broken. The build gate is
// `npm run validate:pack`; this is the second line of defence.
if (import.meta.env.DEV) {
  const issues = [
    ...validateRegistry(packs, defaultSlug),
    ...packs.flatMap((p) => validatePack(p)),
  ].filter((i) => i.level === 'error');
  if (issues.length) {
    console.error('Pack validation errors:', issues);
  }
}

const root = document.getElementById('app');
if (!root) throw new Error('#app not found');

const store = createStore({
  knownSlugs: knownSlugs(),
  defaultSlug,
  resolve: resolvePack,
});

function render(): void {
  const state = store.get();
  root!.innerHTML = renderApp(store.pack(), state, packs);
  if (state.openDrawer) {
    const drawer = root!.querySelector<HTMLElement>('.drawer');
    drawer?.focus();
  }
}

// --------------------------------------------------------------- interaction

root.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;

  const project = t.closest<HTMLElement>('[data-project]');
  if (project) {
    store.set({ packSlug: project.dataset['project']! });
    return;
  }

  const outcome = t.closest<HTMLElement>('[data-outcome]');
  if (outcome) {
    store.toggleOutcome(outcome.dataset['outcome']!);
    return;
  }

  if (t.closest('[data-clear-outcomes]')) {
    store.clearOutcomes();
    return;
  }

  const scenario = t.closest<HTMLElement>('[data-scenario]');
  if (scenario) {
    store.set({ scenarioId: scenario.dataset['scenario']! });
    return;
  }

  const mode = t.closest<HTMLElement>('[data-mode]');
  if (mode) {
    store.set({ mode: mode.dataset['mode'] as 'plain' | 'analyst' });
    return;
  }

  const drawer = t.closest<HTMLElement>('[data-drawer]');
  if (drawer) {
    store.set({ openDrawer: drawer.dataset['drawer']! });
    return;
  }

  if (t.closest('[data-close-drawer]')) {
    store.set({ openDrawer: null });
    return;
  }

  const reset = t.closest<HTMLElement>('[data-reset-override]');
  if (reset) {
    store.clearOverride(reset.dataset['resetOverride']!);
    return;
  }

  if (t.closest('[data-reset-all]')) {
    store.resetOverrides();
    return;
  }

  if (t.closest('[data-copy-link]')) {
    void navigator.clipboard?.writeText(shareUrl(store.pack(), store.get(), store.route()));
    return;
  }

  const cite = t.closest<HTMLElement>('[data-copy-citation]');
  if (cite) {
    void navigator.clipboard?.writeText(citationFor(cite.dataset['copyCitation']!));
  }
});

// Sliders fire continuously — replaceState, so back undoes the decision rather
// than each pixel of the drag.
root.addEventListener('input', (e) => {
  const el = e.target as HTMLInputElement;
  const id = el.dataset['override'];
  if (!id) return;
  store.setOverride(id, Number(el.value));
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && store.get().openDrawer) {
    store.set({ openDrawer: null });
  }
});

/** Paste-ready block: value, sources, retrieval dates, permalink. */
function citationFor(metricId: string): string {
  const pack = store.pack();
  const metric = pack.metrics.find((m) => m.id === metricId);
  if (!metric) return '';
  const state = store.get();
  const result = evaluateMetric(pack, metric, {
    scenarioId: state.scenarioId,
    params: {},
    overrides: state.overrides,
  });
  const prov = provenanceOf(parseCached(metric.expr), pack);
  const scenario = pack.scenarios.find((s) => s.id === state.scenarioId)!;

  const value = isUndisclosed(result.value)
    ? 'Not publicly disclosed'
    : formatRange(result.value as Range, metric.unit, result.sigFigs);

  const sources = prov.sourceIds
    .map((id) => pack.sources.find((s) => s.id === id))
    .filter(Boolean)
    .map((s) => `  - ${s!.publisher}, "${s!.title}" — ${s!.url} (retrieved ${s!.retrievedAt})`)
    .join('\n');

  return [
    `${metric.label} — ${scenario.label}`,
    `${value}`,
    ``,
    `Formula: ${metric.expr}`,
    `Method: ${metric.methodology}`,
    ``,
    `Sources:`,
    sources,
    ``,
    `Limitations:`,
    ...metric.caveats.map((c) => `  - ${c}`),
    ``,
    `${shareUrl(pack, state, store.route())}`,
    `whatweget.org · data version ${pack.meta.dataVersion}, ${pack.meta.lastUpdated}`,
  ].join('\n');
}

store.subscribe(render);
render();
